class DrawerCart extends HTMLElement {
  constructor() {
    super();

    this.overlay = this.querySelector('.drawer-cart__overlay');
    this.container = this.querySelector('.drawer-cart__container');
    this.itemsContainer = this.querySelector('.drawer-cart__items');
    this.footerContainer = this.querySelector('.drawer-cart__footer');
    this.closeButtons = this.querySelectorAll('[data-drawer-close]');
    this.upsellsContainer = this.querySelector('[data-upsells-container]');

    this.upsellsLoaded = false;
    this.currentItemCount = 0;

    this.bindEvents();
    this.setupCartIconTrigger();
  }

  bindEvents() {
    // Close on overlay click
    this.overlay?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.close();
    });

    // Close on close buttons
    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        this.close();
      });
    });

    // Close on click outside drawer container (robust fallback)
    this.addEventListener('click', (e) => {
      if (e.target === this && this.classList.contains('active')) {
        this.close();
      }
    });

    // Close on Escape key
    document.addEventListener('keyup', (e) => {
      if (e.code === 'Escape' && this.classList.contains('active')) {
        this.close();
      }
    });

    // Event delegation for quantity controls AND addon checkboxes
    this.addEventListener('click', (e) => {
      const target = e.target.closest('button');

      if (target) {
        if (target.hasAttribute('data-quantity-plus')) {
          e.preventDefault();
          this.handleQuantityPlus(target);
        } else if (target.hasAttribute('data-quantity-minus')) {
          e.preventDefault();
          this.handleQuantityMinus(target);
        } else if (target.hasAttribute('data-remove-item')) {
          e.preventDefault();
          this.handleRemoveItem(target);
        }
      }
    });

    // Event delegation for addon checkboxes
    this.addEventListener('change', (e) => {
      if (e.target.classList.contains('addon-checkbox-drawer')) {
        this.handleAddonChange(e.target);
      }
    });
  }

  setupCartIconTrigger() {
    const cartIcon = document.querySelector('#cart-icon-bubble');
    if (cartIcon) {
      cartIcon.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    }
  }

  open() {
    // Hide sticky mobile CTA when drawer opens (prevents blocking checkout button)
    const stickyCta = document.getElementById('sticky-mobile-cta');
    if (stickyCta) {
      stickyCta.style.display = 'none';
    }

    this.classList.add('active');
    document.body.classList.add('drawer-cart-open');

    // Wait for slide-in animation to complete
    this.container.addEventListener('transitionend', () => {
      trapFocus(this.container);
    }, { once: true });

    // Lazy load upsells if not already loaded
    if (!this.upsellsLoaded) {
      this.loadRecommendations();
    }
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('drawer-cart-open');
    removeTrapFocus();

    // Show sticky mobile CTA again when drawer closes
    const stickyCta = document.getElementById('sticky-mobile-cta');
    if (stickyCta) {
      stickyCta.style.display = '';
    }
  }

  handleQuantityPlus(button) {
    const line = parseInt(button.dataset.index);
    const itemElement = button.closest('.cart-item-drawer');
    const currentQty = parseInt(itemElement.querySelector('[data-quantity-value]').textContent);

    this.updateQuantity(line, currentQty + 1, button);
  }

  handleQuantityMinus(button) {
    const line = parseInt(button.dataset.index);
    const itemElement = button.closest('.cart-item-drawer');
    const currentQty = parseInt(itemElement.querySelector('[data-quantity-value]').textContent);

    if (currentQty > 1) {
      this.updateQuantity(line, currentQty - 1, button);
    }
  }

  handleRemoveItem(button) {
    const line = parseInt(button.dataset.index);
    this.updateQuantity(line, 0, button);
  }

  updateQuantity(line, quantity, button) {
    // Disable button during request
    button.disabled = true;
    this.itemsContainer?.classList.add('loading');

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map(section => section.id),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, {
      ...fetchConfig('javascript'),
      body
    })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        this.updateCart(parsedState);
        this.updateLiveRegions(line, parsedState.item_count);

        // Reload recommendations after cart update
        if (parsedState.item_count > 0 && !this.upsellsLoaded) {
          this.loadRecommendations();
        }

        // Dispatch quantityChanged event for addon sync
        const event = new CustomEvent('quantityChanged', {
          detail: {
            quantity: parsedState.item_count,
            reloadCart: () => this.refreshCart()
          }
        });
        document.dispatchEvent(event);
      })
      .catch((e) => {
        console.error(e);
        this.showError('Noget gik galt. Prøv igen.');
      })
      .finally(() => {
        button.disabled = false;
        this.itemsContainer?.classList.remove('loading');
      });
  }

  updateCart(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const elementToReplace = document.getElementById(section.id) || document.querySelector(section.selector);

      if (elementToReplace) {
        const sectionHTML = parsedState.sections[section.id];

        elementToReplace.innerHTML = this.getSectionInnerHTML(
          sectionHTML,
          section.selector
        );
      }
    });

    // Reset upsells flag when cart becomes empty or when going from empty to having items
    const wasEmpty = this.currentItemCount === 0;
    const isNowEmpty = parsedState.item_count === 0;
    const isNowFilled = parsedState.item_count > 0 && wasEmpty;

    if (isNowEmpty || isNowFilled) {
      this.upsellsLoaded = false;
    }

    // Update current item count
    this.currentItemCount = parsedState.item_count;
  }

  getSectionsToRender() {
    return [
      {
        id: 'drawer-cart-items',
        selector: 'body'
      },
      {
        id: 'drawer-cart-body',
        selector: 'body'
      },
      {
        id: 'drawer-cart-footer',
        selector: 'body'
      },
      {
        id: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');

    const shopifySection = parsed.querySelector('.shopify-section');

    if (shopifySection) {
      return shopifySection.innerHTML;
    }

    const element = parsed.querySelector(selector);
    if (element) {
      return element.innerHTML;
    }

    return parsed.body.innerHTML;
  }

  updateLiveRegions(line, itemCount) {
    const lineItemError = document.getElementById(`Line-item-error-${line}`);
    if (lineItemError) {
      lineItemError.querySelector('.cart-item__error-text').innerHTML =
        window.cartStrings.quantityError.replace('[quantity]', itemCount);
    }

    this.updateCartCount(itemCount);
  }

  updateCartCount(itemCount) {
    const cartCountBubble = document.querySelector('.cart-count-bubble');
    if (cartCountBubble) {
      cartCountBubble.textContent = itemCount;
      if (itemCount > 0) {
        cartCountBubble.classList.remove('hidden');
      } else {
        cartCountBubble.classList.add('hidden');
      }
    }
  }

  refreshCart() {
    const sectionIds = this.getSectionsToRender().map(s => s.id).join(',');
    fetch(`${window.Shopify.routes.root}?sections=${sectionIds}`)
      .then(response => {
        if (!response.ok) throw new Error('Section Rendering API failed');
        return response.json();
      })
      .then(sections => {
        this.getSectionsToRender().forEach(section => {
          const elementToReplace = document.getElementById(section.id) || document.querySelector(section.selector);
          if (elementToReplace && sections[section.id]) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
          }
        });
      })
      .catch(() => {
        // Fallback: fetch the entire page
        fetch(window.location.href)
          .then(response => response.text())
          .then(html => {
            const doc = new DOMParser().parseFromString(html, 'text/html');

            const newItems = doc.querySelector('.drawer-cart__items');
            if (newItems && this.itemsContainer) {
              this.itemsContainer.innerHTML = newItems.innerHTML;
            }

            const newAddons = doc.querySelector('.drawer-cart__addons');
            const currentAddons = this.querySelector('.drawer-cart__addons');
            if (newAddons && currentAddons) {
              currentAddons.innerHTML = newAddons.innerHTML;
            }

            const newFooter = doc.querySelector('.drawer-cart__footer');
            if (newFooter && this.footerContainer) {
              this.footerContainer.innerHTML = newFooter.innerHTML;
            }
          });
      });
  }

  async loadRecommendations() {
    const upsellsContainer = this.querySelector('[data-upsells-container]');
    const upsellsSection = this.querySelector('.drawer-cart__upsells');

    if (!upsellsContainer) {
      return;
    }

    // Show upsells section
    if (upsellsSection) {
      upsellsSection.style.display = '';
    }

    // Get first product ID from cart
    const firstProductElement = this.querySelector('[data-product-id]');

    if (!firstProductElement) {
      this.loadFallbackCollection();
      return;
    }

    const productId = firstProductElement.dataset.productId;

    try {
      const response = await fetch(
        `/recommendations/products.json?product_id=${productId}&limit=4&intent=related`
      );

      if (!response.ok) throw new Error('Recommendations fetch failed');

      const data = await response.json();

      if (data.products && data.products.length > 0) {
        this.renderRecommendations(data.products);
      } else {
        this.loadFallbackCollection();
      }

      this.upsellsLoaded = true;
    } catch (error) {
      console.error('Recommendations error:', error);
      this.loadFallbackCollection();
    }
  }

  renderRecommendations(products) {
    const upsellsContainer = this.querySelector('[data-upsells-container]');
    if (!upsellsContainer) {
      return;
    }

    const productsHTML = products.map(product => {
      let price;
      if (Shopify.formatMoney && theme.moneyFormat) {
        price = Shopify.formatMoney(product.price, theme.moneyFormat);
      } else {
        const amount = product.price / 100;
        const formatted = amount.toLocaleString('da-DK', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        price = `${formatted} kr.`;
      }

      const imageSrc = product.featured_image || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';

      return `
        <a href="${product.url}" class="drawer-cart__upsell-card">
          <img
            src="${imageSrc}"
            alt="${this.escapeHtml(product.title)}"
            loading="lazy"
            width="200"
            height="200"
          />
          <div class="drawer-cart__upsell-card-content">
            <h4>${this.escapeHtml(product.title)}</h4>
            <span class="price">${price}</span>
          </div>
        </a>
      `;
    }).join('');

    upsellsContainer.innerHTML = productsHTML;
  }

  async loadFallbackCollection() {
    const upsellsContainer = this.querySelector('[data-upsells-container]');
    if (!upsellsContainer) {
      return;
    }

    try {
      const response = await fetch('/collections/all/products.json?limit=4');

      if (!response.ok) throw new Error('Fallback collection fetch failed');

      const data = await response.json();

      if (data.products && data.products.length > 0) {
        this.renderRecommendations(data.products);
      } else {
        upsellsContainer.innerHTML = '';
      }

      this.upsellsLoaded = true;
    } catch (error) {
      console.error('Fallback collection error:', error);
      upsellsContainer.innerHTML = '';
      this.upsellsLoaded = true;
    }
  }

  escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  }

  handleAddToCart(response) {
    if (response.sections) {
      this.updateCart(response);

      if (!this.upsellsLoaded) {
        this.loadRecommendations();
      }

      // GoPro is opt-in: only added when the customer actively ticks the box
      // (see handleAddonChange). No silent auto-add on add-to-cart.
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  showError(message) {
    let errorEl = this.querySelector('.drawer-cart__error');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'drawer-cart__error';
      errorEl.setAttribute('role', 'alert');
      errorEl.style.cssText = 'background:#fef2f2;color:#991b1b;padding:10px 16px;font-size:0.9rem;border-radius:6px;margin:8px 16px;';
      this.container.insertBefore(errorEl, this.container.firstChild.nextSibling);
    }
    errorEl.textContent = message;
    errorEl.hidden = false;
    setTimeout(() => { errorEl.hidden = true; }, 5000);
  }

  handleAddonChange(checkbox) {
    if (this.isUpdatingAddon) return;
    this.isUpdatingAddon = true;

    const addonId = checkbox.dataset.addonId;
    const isChecked = checkbox.checked;
    const isGoPro = checkbox.id === 'gopro-addon-drawer';

    let quantity = 0;
    if (isChecked) {
      if (isGoPro) {
        const addonCheckboxes = this.querySelectorAll('.addon-checkbox-drawer');
        const addonVariantIds = Array.from(addonCheckboxes).map(cb => cb.dataset.addonId);

        const allItems = this.querySelectorAll('.cart-item-drawer[data-variant-id]');
        quantity = Array.from(allItems).reduce((total, item) => {
          const variantId = item.dataset.variantId;
          if (addonVariantIds.includes(variantId)) {
            return total;
          }
          const qtyElement = item.querySelector('[data-quantity-value]');
          const itemQty = qtyElement ? parseInt(qtyElement.textContent, 10) : 0;
          return total + itemQty;
        }, 0);
      } else {
        quantity = 1;
      }
    }

    const body = JSON.stringify({
      updates: { [addonId]: quantity },
      attributes: {
        [checkbox.name.replace('attributes[', '').replace(']', '')]: isChecked ? true : ''
      }
    });

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body
    })
    .then(response => response.json())
    .then(() => {
      this.refreshCart();
      this.isUpdatingAddon = false;
    })
    .catch(error => {
      console.error('Addon update error:', error);
      this.isUpdatingAddon = false;
    });
  }

  syncGoProAddon(detail) {
    if (this.isUpdatingAddon) return;

    const goProCheckbox = this.querySelector('#gopro-addon-drawer');
    if (!goProCheckbox || !goProCheckbox.checked) return;

    this.isUpdatingAddon = true;
    const addonId = goProCheckbox.dataset.addonId;

    const addonCheckboxes = this.querySelectorAll('.addon-checkbox-drawer');
    const addonVariantIds = Array.from(addonCheckboxes).map(cb => cb.dataset.addonId);

    const allItems = this.querySelectorAll('.cart-item-drawer[data-variant-id]');
    const quantity = Array.from(allItems).reduce((total, item) => {
      const variantId = item.dataset.variantId;
      if (addonVariantIds.includes(variantId)) {
        return total;
      }
      const qtyElement = item.querySelector('[data-quantity-value]');
      const itemQty = qtyElement ? parseInt(qtyElement.textContent, 10) : 0;
      return total + itemQty;
    }, 0);

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates: { [addonId]: quantity },
        attributes: { 'gopro-addon': quantity > 0 ? true : '' }
      })
    })
    .then(() => {
      if (detail.reloadCart) {
        detail.reloadCart();
      }
      this.isUpdatingAddon = false;
    })
    .catch(error => {
      console.error('GoPro sync error:', error);
      this.isUpdatingAddon = false;
    });
  }
}

customElements.define('drawer-cart', DrawerCart);
